/**
 * Calendar-style slot picker - like Google Calendar.
 * Left: Month calendar view (custom, no MUI X); Right: Time slots for selected date.
 */
import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  useTheme,
  Chip,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import dayjs from 'dayjs';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const CalendarSlotPicker = ({ slots = [], selectedSlot, onSelectSlot, disabled }) => {
  const theme = useTheme();
  const today = dayjs();

  // Group slots by date (YYYY-MM-DD)
  const slotsByDate = useMemo(() => {
    const map = {};
    (slots || []).forEach((slot) => {
      const d = dayjs(slot.start).format('YYYY-MM-DD');
      if (!map[d]) map[d] = [];
      map[d].push(slot);
    });
    Object.keys(map).forEach((d) => {
      map[d].sort((a, b) => new Date(a.start) - new Date(b.start));
    });
    return map;
  }, [slots]);

  const datesWithSlots = useMemo(() => Object.keys(slotsByDate).sort(), [slotsByDate]);
  const minDate = datesWithSlots.length ? dayjs(datesWithSlots[0]) : today;
  const maxDate = datesWithSlots.length ? dayjs(datesWithSlots[datesWithSlots.length - 1]) : today.add(14, 'day');

  const getInitialDate = () => {
    if (datesWithSlots.length) {
      const next = datesWithSlots.find((d) => !dayjs(d).isBefore(today, 'day'));
      return next ? dayjs(next) : dayjs(datesWithSlots[0]);
    }
    return today;
  };

  const [viewMonth, setViewMonth] = useState(() => getInitialDate());
  const [selectedDate, setSelectedDate] = useState(getInitialDate);

  useEffect(() => {
    if (datesWithSlots.length && selectedDate) {
      const key = selectedDate.format('YYYY-MM-DD');
      if (!slotsByDate[key]?.length) {
        const next = datesWithSlots.find((d) => !dayjs(d).isBefore(today, 'day'));
        const d = next ? dayjs(next) : dayjs(datesWithSlots[0]);
        setSelectedDate(d);
        setViewMonth(d);
      }
    }
  }, [slots.length]);

  const slotsForSelectedDate = useMemo(() => {
    const key = selectedDate?.format('YYYY-MM-DD');
    return slotsByDate[key] || [];
  }, [selectedDate, slotsByDate]);

  const handleDateClick = (d) => {
    setSelectedDate(d);
    onSelectSlot?.(null);
  };

  const shouldDisableDate = (date) => {
    const key = date.format('YYYY-MM-DD');
    return !slotsByDate[key]?.length || date.isBefore(today, 'day');
  };

  // Build calendar grid for viewMonth
  const calendarDays = useMemo(() => {
    const start = viewMonth.startOf('month');
    const end = viewMonth.endOf('month');
    const startPad = start.day(); // 0 = Sunday
    const daysInMonth = end.date();
    const rows = [];
    let week = Array(7).fill(null);
    let dayNum = 1;
    for (let i = 0; i < 6; i++) {
      for (let j = 0; j < 7; j++) {
        if (i === 0 && j < startPad) {
          week[j] = null;
        } else if (dayNum <= daysInMonth) {
          const d = viewMonth.date(dayNum);
          week[j] = d;
          dayNum++;
        } else {
          week[j] = null;
        }
      }
      rows.push([...week]);
      week = Array(7).fill(null);
    }
    return rows;
  }, [viewMonth]);

  const canPrev = viewMonth.startOf('month').isAfter(minDate);
  const canNext = viewMonth.endOf('month').isBefore(maxDate);

  return (
    <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>{selectedSlot && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: 'success.lighter', borderRadius: 1.5, border: '1px solid', borderColor: 'success.light' }}>
          <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20, flexShrink: 0 }} />
          <Typography variant="body2" sx={{ color: 'success.dark', fontWeight: 600 }}>
            Selected: {dayjs(selectedSlot.start).format('ddd, MMM D [at] h:mm A')}
          </Typography>
        </Box>
      )}
    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { sm: 'flex-start' } }}>
      {/* Calendar - left side */}
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          minWidth: 280,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <IconButton
            size="small"
            onClick={() => setViewMonth((m) => m.subtract(1, 'month'))}
            disabled={!canPrev}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {viewMonth.format('MMMM YYYY')}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setViewMonth((m) => m.add(1, 'month'))}
            disabled={!canNext}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
          {WEEKDAYS.map((wd) => (
            <Typography key={wd} variant="caption" sx={{ textAlign: 'center', fontWeight: 600, color: 'text.secondary', py: 0.5 }}>
              {wd}
            </Typography>
          ))}
          {calendarDays.flat().map((d, idx) =>
            d ? (
              <Button
                key={d.format('YYYY-MM-DD')}
                size="small"
                disableRipple
                disabled={shouldDisableDate(d)}
                onClick={() => handleDateClick(d)}
                sx={{
                  minWidth: 32,
                  height: 32,
                  p: 0,
                  fontSize: '0.8125rem',
                  ...(selectedDate && d.isSame(selectedDate, 'day') && {
                    bgcolor: theme.palette.primary.main,
                    color: 'white',
                    '&:hover': { bgcolor: theme.palette.primary.dark, color: 'white' },
                  }),
                  ...(slotsByDate[d.format('YYYY-MM-DD')]?.length && !selectedDate?.isSame(d, 'day') && {
                    bgcolor: 'rgba(26, 56, 255, 0.08)',
                    fontWeight: 600,
                  }),
                }}
              >
                {d.date()}
              </Button>
            ) : (
              <Box key={`empty-${idx}`} sx={{ width: 32, height: 32 }} />
            )
          )}
        </Box>
      </Paper>

      {/* Time slots - right side */}
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          minWidth: 220,
          maxHeight: 320,
          overflow: 'auto',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <AccessTimeIcon sx={{ fontSize: 18 }} />
          {selectedDate?.format('ddd, MMM D')}
        </Typography>
        {slotsForSelectedDate.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No available times this day. Select another date.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {slotsForSelectedDate.map((slot) => {
              const isSelected = selectedSlot?.start === slot.start;
              return (
                <Button
                  key={slot.start}
                  variant={isSelected ? 'contained' : 'outlined'}
                  size="small"
                  fullWidth
                  onClick={() => onSelectSlot?.(isSelected ? null : slot)}
                  disabled={disabled}
                  sx={{
                    py: 1,
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    fontWeight: isSelected ? 600 : 500,
                    fontSize: '0.875rem',
                  }}
                >
                  {dayjs(slot.start).format('h:mm A')}
                </Button>
              );
            })}
          </Box>
        )}
      </Paper>
    </Box>
    </Box>
  );
};

export default CalendarSlotPicker;
